import React, { useState, useEffect, useRef } from "react";
//import { useNavigate } from "react-router-dom";
import useRouting from "../hooks/useRouting";
import usePageTracking from "../hooks/usePageTracking";
import { trackEvent } from "../utils/trackEvent";
import environment from "../environment";
import axios from "axios";
import { getHeaders, getStorage, handleApiError } from "../utils/apiUtils";
import Header from "./Header";
import "./AddressDetails.css";
import address_detail from "../assets/images/address_detail.svg";

const AddressDetails = () => {
  const hasFetchedRef = useRef(false);
  // Helper function to capitalize first letter
  const capitalizeWord = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Current Address State
  const [currentAddress, setCurrentAddress] = useState("");
  const [currentStreet, setCurrentStreet] = useState("");
  const [currentPinCode, setCurrentPinCode] = useState("");
  const [currentState, setCurrentState] = useState("");
  const [currentCity, setCurrentCity] = useState("");
  const [currentArea, setCurrentArea] = useState("");
  const [currentLocalityOptions, setCurrentLocalityOptions] = useState([]);
  const [pincodeStatus, setPincodeStatus] = useState("");

  // Permanent Address State
  const [permanentAddress, setPermanentAddress] = useState("");
  const [permanentStreet, setPermanentStreet] = useState("");
  const [permanentPinCode, setPermanentPinCode] = useState("");
  const [permanentState, setPermanentState] = useState("");
  const [permanentCity, setPermanentCity] = useState("");
  const [permanentArea, setPermanentArea] = useState("");
  const [permanentLocalityOptions, setPermanentLocalityOptions] = useState([]);
  const [permanentPincodeStatus, setPermanentPincodeStatus] = useState("");

  // Housing Details State
  const [housingType, setHousingType] = useState("");
  const [durationOfStay, setDurationOfStay] = useState("");

  // Checkbox for same address
  const [isSameAddress, setIsSameAddress] = useState(false);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  //const navigate = useNavigate();
  const { navigateWithContext, navigateBack } = useRouting();

  // Track page visit
  usePageTracking("address-details");
  useEffect(() => { trackEvent("loan_address_details_viewed"); }, []);

 
  // Watch for current pin code changes and auto-fill state/city/area
  useEffect(() => {
    const fetchCurrentLocation = async () => {
      if (currentPinCode && currentPinCode.length === 6) {
        setPincodeStatus("Please wait fetching state & city ...");
        const locationData = await fetchLocationFromPinCode(currentPinCode);
        if (locationData && (locationData.state || locationData.city)) {
          setCurrentState(locationData.state ? capitalizeWord(locationData.state) : "");
          setCurrentCity(locationData.city ? capitalizeWord(locationData.city) : "");
          setCurrentLocalityOptions(locationData.localityOptions || []);
          setCurrentArea(locationData.area ? capitalizeWord(locationData.area) : "");
          setPincodeStatus("");
        } else {
          setPincodeStatus("Failed to fetch city/state");
        }
      } else {
        setCurrentState("");
        setCurrentCity("");
        setCurrentArea("");
        setCurrentLocalityOptions([]);
        setPincodeStatus("");
      }
    };

    const timeoutId = setTimeout(fetchCurrentLocation, 500);
    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPinCode]);

  // Watch for permanent pin code changes and auto-fill state/city/area
  useEffect(() => {
    const fetchPermanentLocation = async () => {
      if (permanentPinCode && permanentPinCode.length === 6 && !isSameAddress) {
        setPermanentPincodeStatus("Please wait fetching state & city ...");
        const locationData = await fetchLocationFromPinCode(permanentPinCode);
        if (locationData && (locationData.state || locationData.city)) {
          setPermanentState(locationData.state ? capitalizeWord(locationData.state) : "");
          setPermanentCity(locationData.city ? capitalizeWord(locationData.city) : "");
          setPermanentLocalityOptions(locationData.localityOptions || []);
          setPermanentArea(locationData.area ? capitalizeWord(locationData.area) : "");
          setPermanentPincodeStatus("");
        } else {
          setPermanentPincodeStatus("Failed to fetch city/state");
        }
      } else if (!isSameAddress) {
        setPermanentState("");
        setPermanentCity("");
        setPermanentArea("");
        setPermanentLocalityOptions([]);
        setPermanentPincodeStatus("");
      }
    };

    const timeoutId = setTimeout(fetchPermanentLocation, 500);
    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permanentPinCode, isSameAddress]);

  // Fetch current address and housing details
  const fetchCurrentAddressDetails = async () => {
    try {
      const applicationId = getStorage("application_id");
      if (!applicationId) {
        console.error("Application ID not found in localStorage");
        return;
      }

      const endpoint = `${environment.apiurl}applications/${applicationId}/view/data/?allowed=personal_resident_address`;
      const response = await axios.get(endpoint, { headers: getHeaders() });

      if (
        response?.data?.msg &&
        response.data?.msg?.personal_resident_address
      ) {
        const residentData = response.data.msg.personal_resident_address;

        // Prefill current address fields
        if (residentData.address_line_1)
          setCurrentAddress(residentData.address_line_1);
        if (residentData.address_line_2)
          setCurrentStreet(residentData.address_line_2);
        if (residentData.pincode) setCurrentPinCode(residentData.pincode);
        if (residentData.state)
          setCurrentState(capitalizeWord(residentData.state));
        if (residentData.city) setCurrentCity(capitalizeWord(residentData.city));
        if (residentData.landmark)
          setCurrentArea(capitalizeWord(residentData.landmark));

        // Prefill housing details
        if (residentData.ownership_type)
          setHousingType(residentData.ownership_type.toLowerCase());
        if (residentData.vintage) setDurationOfStay(residentData.vintage);
      }
    } catch (error) {
      console.error("Error fetching current address details:", error);
      // Don't show error to user for prefill failures
    }
  };

  // Fetch permanent address details
  const fetchPermanentAddressDetails = async () => {
    try {
      const applicationId = getStorage("application_id");
      if (!applicationId) {
        console.error("Application ID not found in localStorage");
        return;
      }

      const endpoint = `${environment.apiurl}applications/${applicationId}/permanent_address_details/`;
      const response = await axios.get(endpoint, { headers: getHeaders() });

      if (response?.data?.msg) {
        const permanentData = response.data.msg;

        // Prefill permanent address fields
        if (permanentData.address_line_1)
          setPermanentAddress(permanentData.address_line_1);
        if (permanentData.address_line_2)
          setPermanentStreet(permanentData.address_line_2);
        if (permanentData.pincode) setPermanentPinCode(permanentData.pincode);
        if (permanentData.state)
          setPermanentState(capitalizeWord(permanentData.state));
        if (permanentData.city)
          setPermanentCity(capitalizeWord(permanentData.city));
        if (permanentData.area)
          setPermanentArea(capitalizeWord(permanentData.area));
      }
    } catch (error) {
      console.error("Error fetching permanent address details:", error);
      // Don't show error to user for prefill failures
    }
  };

  // Combined function to fetch all address details
  // const fetchAddressDetails = async () => {
  //   try {
  //     // Fetch both current and permanent address details concurrently
  //     await Promise.all([
  //       fetchCurrentAddressDetails(),
  //       fetchPermanentAddressDetails(),
  //     ]);
  //   } catch (error) {
  //     console.error("Error fetching address details:", error);
  //   }
  // };

  //  useEffect(() => {
  //   fetchAddressDetails();
  // }, [fetchAddressDetails]);

  useEffect(() => {
    // Prevent duplicate API calls in StrictMode
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    
    const fetchAddressDetails = async () => {
      try {
        await Promise.all([
          fetchCurrentAddressDetails(),
          fetchPermanentAddressDetails(),
        ]);
      } catch (error) {
        console.error("Error fetching address details:", error);
      }
    };

    fetchAddressDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Handle same address checkbox
  const handleSameAddressChange = (checked) => {
    setIsSameAddress(checked);
    setPermanentPincodeStatus("");
    if (checked) {
      setPermanentAddress(currentAddress);
      setPermanentStreet(currentStreet);
      setPermanentPinCode(currentPinCode);
      setPermanentState(currentState);
      setPermanentCity(currentCity);
      setPermanentArea(currentArea);
      setPermanentLocalityOptions(currentLocalityOptions);
    } else {
      setPermanentAddress("");
      setPermanentStreet("");
      setPermanentPinCode("");
      setPermanentState("");
      setPermanentCity("");
      setPermanentArea("");
      setPermanentLocalityOptions([]);
    }
  };

  // Update permanent address when current address changes if same address is checked
  useEffect(() => {
    if (isSameAddress) {
      setPermanentAddress(currentAddress);
      setPermanentStreet(currentStreet);
      setPermanentPinCode(currentPinCode);
      setPermanentState(currentState);
      setPermanentCity(currentCity);
      setPermanentArea(currentArea);
      setPermanentLocalityOptions(currentLocalityOptions);
    }
  }, [
    currentAddress,
    currentStreet,
    currentPinCode,
    currentState,
    currentCity,
    currentArea,
    currentLocalityOptions,
    isSameAddress,
  ]);

  // API call for resident address
  const submitResidentAddress = async () => {
    const applicationId = getStorage("application_id");
    if (!applicationId) {
      throw new Error("Application ID not found");
    }

    const payload = {
      address_line_1: currentAddress,
      address_line_2: currentStreet,
      landmark: currentArea,
      city: currentCity,
      state: currentState,
      pincode: currentPinCode,
      ownership_type: housingType,
      vintage: durationOfStay,
    };

    const response = await axios.post(
      `${environment.apiurl}applications/${applicationId}/address/resident/detail/`,
      payload,
      { headers: getHeaders() }
    );

    return response.data;
  };

  // API call for permanent address
  const submitPermanentAddress = async () => {
    const applicationId = getStorage("application_id");
    if (!applicationId) {
      throw new Error("Application ID not found");
    }

    const payload = {
      address_line_1: permanentAddress,
      address_line_2: permanentStreet,
      landmark: permanentArea,
      city: permanentCity,
      state: permanentState,
      pincode: permanentPinCode,
    };

    const response = await axios.post(
      `${environment.apiurl}applications/${applicationId}/address/permanent/detail/`,
      payload,
      { headers: getHeaders() }
    );

    return response.data;
  };

  const handleContinue = async () => {
    trackEvent("loan_address_details_continue_clicked", {
      is_same_address: isSameAddress,
      housing_type: housingType,
    });
    const addressData = {
      currentAddress: {
        address: currentAddress,
        street: currentStreet,
        pinCode: currentPinCode,
        state: currentState,
        city: currentCity,
        area: currentArea,
      },
      permanentAddress: {
        address: permanentAddress,
        street: permanentStreet,
        pinCode: permanentPinCode,
        state: permanentState,
        city: permanentCity,
        area: permanentArea,
      },
      housingDetails: {
        type: housingType,
        durationOfStay: durationOfStay,
      },
      isSameAddress,
    };

    const isCurrentAddressValid =
      currentAddress &&
      currentPinCode &&
      currentState &&
      currentCity &&
      currentArea;
    const isPermanentAddressValid =
      permanentAddress &&
      permanentPinCode &&
      permanentState &&
      permanentCity &&
      permanentArea;
    const isHousingDetailsValid = housingType && durationOfStay;

    if (
      isCurrentAddressValid &&
      isPermanentAddressValid &&
      isHousingDetailsValid
    ) {
      setIsLoading(true);
      setApiError("");

      try {
        // Call both APIs concurrently
        await Promise.all([submitResidentAddress(), submitPermanentAddress()]);

        // Save to localStorage after successful API calls
        localStorage.setItem("addressDetails", JSON.stringify(addressData));

        // Navigate to next step
        navigateWithContext("employment-details");
      } catch (error) {
        console.error("Error submitting address details:", error);
        const errorMessage = handleApiError(error);
        setApiError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBack = () => {
    // Use new routing
    navigateBack("personal-details");
  };

  // Function to fetch location data from postal API
  const fetchLocationFromPinCode = async (pinCode) => {
    if (!pinCode || pinCode.length !== 6) {
      return { state: "", city: "", area: "", localityOptions: [] };
    }

    try {
      const response = await axios.get(
        `${environment.apiurlv3_webapi}v4/pincode/detail/?pincode=${pinCode}`
      );

      const msg = response.data?.msg;
      if (msg && response.data?.metadata?.api_status === true) {
        const locations = msg.locations || [];
        const first = locations[0] || {};
        // district → city, city → locality/area options, state → state
        const localityOptions = [...new Set(locations.map((l) => l.city).filter(Boolean))];
        return {
          state: first.state || "",
          city: first.district || "",
          area: localityOptions[0] || "",
          localityOptions,
        };
      }

      return { state: "", city: "", area: "", localityOptions: [] };
    } catch (error) {
      return { state: "", city: "", area: "", localityOptions: [] };
    }
  };
  // Dynamic arrays that will include both default options and API fetched data
  // const getStatesOptions = () => {
  //   const currentStateFromAPI = currentState
  //     ? [currentState.charAt(0).toUpperCase() + currentState.slice(1)]
  //     : [];
  //   const permanentStateFromAPI = permanentState
  //     ? [permanentState.charAt(0).toUpperCase() + permanentState.slice(1)]
  //     : [];

  //   // Combine and remove duplicates
  //   const allStates = [
  //     ...new Set([...currentStateFromAPI, ...permanentStateFromAPI]),
  //   ];
  //   return allStates;
  // };

  // const getCitiesOptions = () => {
  //   const currentCityFromAPI = currentCity
  //     ? [currentCity.charAt(0).toUpperCase() + currentCity.slice(1)]
  //     : [];
  //   const permanentCityFromAPI = permanentCity
  //     ? [permanentCity.charAt(0).toUpperCase() + permanentCity.slice(1)]
  //     : [];

  //   // Include both current and permanent cities, and remove duplicates
  //   const allCities = [
  //     ...new Set(
  //       [
  //         ...currentCityFromAPI,
  //         ...permanentCityFromAPI,
  //         // Add the current city value if it's not already in the list
  //         ...(currentCity && !currentCityFromAPI.includes(currentCity)
  //           ? [currentCity]
  //           : []),
  //         ...(permanentCity && !permanentCityFromAPI.includes(permanentCity)
  //           ? [permanentCity]
  //           : []),
  //       ].filter(Boolean)
  //     ), // Remove any empty strings
  //   ];

  //   return allCities;
  // };

  // const getAreasOptions = () => {
  //   const defaultAreas = [
  //     "Chanderlok",
  //     "Sector 15",
  //     "Sector 18",
  //     "Mayur Vihar",
  //   ];
  //   const currentAreaFromAPI = currentArea
  //     ? [currentArea.charAt(0).toUpperCase() + currentArea.slice(1)]
  //     : [];
  //   const permanentAreaFromAPI = permanentArea
  //     ? [permanentArea.charAt(0).toUpperCase() + permanentArea.slice(1)]
  //     : [];

  //   // Combine and remove duplicates
  //   const allAreas = [
  //     ...new Set([
  //       ...defaultAreas,
  //       ...currentAreaFromAPI,
  //       ...permanentAreaFromAPI,
  //     ]),
  //   ];
  //   return allAreas;
  // };
  const housingTypes = [
    { key: "Own", value: "o" },
    { key: "Rented", value: "r" },
    { key: "Family Owned", value: "f" },
    { key: "Company Provided", value: "c" },
  ];
  const durationOptions = [
    { label: "0 - 3 Months", value: 3 },
    { label: "3 - 6 Months", value: 6 },
    { label: "6 - 12 Months", value: 12 },
    { label: "1 - 2 Years", value: 24 },
    { label: "2+ Years", value: 48 },
  ];

  const isFormValid =
    currentAddress &&
    currentPinCode &&
    currentState &&
    currentCity &&
    currentArea &&
    permanentAddress &&
    permanentPinCode &&
    permanentState &&
    permanentCity &&
    permanentArea &&
    housingType &&
    durationOfStay;

  return (
    <div className="loan-container">
      <Header showBackButton={true} onBackClick={handleBack} />

      <div className="main-content">
        <div className="hero-section">
          <div className="hero-icon">
            <img src={address_detail} alt="Address Details" />
          </div>
          <h1 className="hero-title">
            Please share your
            <br />
            address details
          </h1>
        </div>

        <div className="form-section">
          {/* Current Address Section */}
          <div className="address-detail-container">
            <h2 className="section-title" style={{ marginBottom: "16px" }}>
              Current Address
            </h2>
            <div className="address-detail-form-group">
              <div className="form-group-container">
                <label className="form-label">
                  Current Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="House No. 10"
                  value={currentAddress}
                  onChange={(e) => setCurrentAddress(e.target.value)}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label">Street No.</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Street No. 4"
                  value={currentStreet}
                  onChange={(e) => setCurrentStreet(e.target.value)}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label">Pin Code <span className="text-red-500">*</span></label>
                <div className="pincode-input-container">
                  {/* <input
                    type="text"
                    className="form-input"
                    placeholder="400001"
                    value={currentPinCode}
                    onChange={(e) => setCurrentPinCode(e.target.value)}
                    maxLength={6}
                    style={{ paddingRight: isPinCodeLoading ? '40px' : '12px' }}
                  />
                  {isPinCodeLoading && (
                    <div className="pincode-loader">
                      <div className="loader"></div>
                    </div>
                  )} */}
                  <input
                    type="text"
                    className="form-input"
                    placeholder="400001"
                    value={currentPinCode}
                    maxLength={6}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCurrentPinCode(val);
                      if (val.length !== 6) {
                        setPincodeStatus("");
                      }
                    }}
                  />

                  {/* Blue fetch message */}
                  {pincodeStatus && (
                    <p className="pincode-status">{pincodeStatus}</p>
                  )}
                </div>
              </div>

              {/* <div className="form-group-container">
                <label className="form-label">State <span className="text-red-500">*</span></label>
                <select
                  className="form-select"
                  value={currentState}
                  onChange={(e) => setCurrentState(e.target.value)}
                >
                  <option value="">Select State</option>
                  {getStatesOptions().map((state, index) => (
                    <option key={index} value={state.toLowerCase()}>
                      {state}
                    </option>
                  ))}
                </select>
              </div> */}
              <div className="form-group-container">
                <label className="form-label">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="State"
                  value={currentState}
                  onChange={(e) => setCurrentState(capitalizeWord(e.target.value))}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="City"
                  value={currentCity}
                  onChange={(e) => setCurrentCity(capitalizeWord(e.target.value))}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label">
                  Area, Locality <span className="text-red-500">*</span>
                </label>
                {currentLocalityOptions.length > 0 ? (
                  <select
                    className="form-select"
                    value={currentArea}
                    onChange={(e) => setCurrentArea(e.target.value)}
                  >
                    <option value="">Select Locality</option>
                    {currentLocalityOptions.map((loc, i) => (
                      <option key={i} value={capitalizeWord(loc)}>
                        {capitalizeWord(loc)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Area"
                    value={currentArea}
                    onChange={(e) => setCurrentArea(capitalizeWord(e.target.value))}
                  />
                )}
              </div>

              {/* Same Address Checkbox */}
              <div className="checkbox-container" style={{ marginTop: "16px" }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isSameAddress}
                    onChange={(e) => handleSameAddressChange(e.target.checked)}
                    className="checkbox-input-address"
                  />
                  <span className="checkbox-custom"></span>
                  Permanent address is same as current address
                </label>
              </div>
            </div>
          </div>

          {/* Permanent Address Section */}
          <div className="address-detail-container">
            <h2 className="section-title" style={{ marginBottom: "16px" }}>
              Permanent Address
            </h2>
            <div className="address-detail-form-group">
              <div className="form-group-container">
                <label className="form-label">
                  Permanent Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="House No. 10"
                  value={permanentAddress}
                  onChange={(e) => setPermanentAddress(e.target.value)}
                  disabled={isSameAddress}
                />
              </div>
              <div className="form-group-container">
                <label className="form-label">Street No.</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Street No. 4"
                  value={permanentStreet}
                  onChange={(e) => setPermanentStreet(e.target.value)}
                  disabled={isSameAddress}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label">
                  Pin Code <span className="text-red-500">*</span>
                </label>
                <div className="pincode-input-container">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="400001"
                    value={permanentPinCode}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPermanentPinCode(val);
                      if (val.length !== 6) {
                        setPermanentPincodeStatus("");
                      }
                    }}
                    maxLength={6}
                    disabled={isSameAddress}
                  />
                  {!isSameAddress && permanentPincodeStatus && (
                    <p className="pincode-status">{permanentPincodeStatus}</p>
                  )}
                </div>
              </div>

              <div className="form-group-container">
                <label className="form-label">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="State"
                  value={permanentState}
                  onChange={(e) => setPermanentState(capitalizeWord(e.target.value))}
                  disabled={isSameAddress}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="City"
                  value={permanentCity}
                  onChange={(e) => setPermanentCity(capitalizeWord(e.target.value))}
                  disabled={isSameAddress}
                />
              </div>

              <div className="form-group-container">
                <label className="form-label">
                  Area, Locality <span className="text-red-500">*</span>
                </label>
                {permanentLocalityOptions.length > 0 ? (
                  <select
                    className="form-select"
                    value={permanentArea}
                    onChange={(e) => setPermanentArea(e.target.value)}
                    disabled={isSameAddress}
                  >
                    <option value="">Select Locality</option>
                    {permanentLocalityOptions.map((loc, i) => (
                      <option key={i} value={capitalizeWord(loc)}>
                        {capitalizeWord(loc)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Area"
                    value={permanentArea}
                    disabled={isSameAddress}
                    onChange={(e) => setPermanentArea(capitalizeWord(e.target.value))}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Housing Details Section */}
          <div className="address-detail-container">
            <h2 className="section-title" style={{ marginBottom: "16px" }}>
              Housing Details
              <br />
              <span className="section-subtitle">(Current Address)</span>
            </h2>
            <div className="address-detail-form-group">
              <div className="form-group-container">
                <label className="form-label">
                  Housing Type <span className="text-red-500">*</span>
                </label>
                <select
                  className="form-select"
                  value={housingType}
                  onChange={(e) => setHousingType(e.target.value)}
                >
                  <option value="">Select Housing Type</option>
                  {housingTypes.map((type, index) => (
                    <option key={index} value={type.value}>
                      {type.key}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group-container">
                <label className="form-label">
                  Duration of Stay <span className="text-red-500">*</span>
                </label>
                <select
                  className="form-select"
                  value={durationOfStay}
                  onChange={(e) => setDurationOfStay(e.target.value)}
                >
                  <option value="">Select Duration</option>
                  {durationOptions.map((duration, index) => (
                    <option key={index} value={duration.value}>
                      {duration.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {apiError && (
        <div
          className="error-message"
          style={{
            color: "#ff4444",
            textAlign: "center",
            padding: "16px",
            marginBottom: "16px",
            backgroundColor: "#ffe6e6",
            borderRadius: "8px",
            margin: "0 16px",
          }}
        >
          {apiError}
        </div>
      )}

      <div className="fixed-button-container">
        <button
          className="continue-button"
          onClick={handleContinue}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? "SUBMITTING..." : "CONTINUE"}
        </button>
      </div>
    </div>
  );
};

export default AddressDetails;
